import React from 'react';
import { DATE_FORMATS, formatDateTime } from '@belong/common';
import { Image, Text } from '@belong/ui';
import clsx from 'clsx';
import { addBusinessDays, isAfter } from 'date-fns';

type Item = {
  id: string;
  description: React.ReactNode;
  icon: string;
  iconClassName?: string;
};

const DAYS_REQUIRED_TO_PROCESS_APPLICATION = 5;

function getMoveInTimingLabel(availableOn?: string): string {
  const soonestDate = addBusinessDays(
    new Date(),
    DAYS_REQUIRED_TO_PROCESS_APPLICATION,
  );
  soonestDate.setHours(0, 0, 0, 0);

  const availableOnDate = new Date(availableOn || '');
  availableOnDate.setHours(0, 0, 0, 0);

  if (!availableOn || isNaN(availableOnDate.getTime())) {
    return ' 5 business days from today.';
  }

  const laterString = `by ${formatDateTime({ dateTime: availableOn, format: DATE_FORMATS.DOTTED_MEDIUM })}.`;

  return isAfter(availableOnDate, soonestDate)
    ? laterString
    : ' 5 business days from today.';
}

const INCOME_REQUIREMENT_VARIABLE = (
  <>
    Applicants must have a{' '}
    <span className="font-semibold">combined gross monthly income</span> of at
    least three times the rent. If additional income is needed, a guarantor can
    be added to the&nbsp;application.
  </>
);

const INCOME_REQUIREMENT_FIXED = (
  <>
    Applicants must have a{' '}
    <span className="font-semibold">combined gross monthly income</span> of at
    least two and a half times the rent. If additional income is needed, a
    guarantor can be added to the&nbsp;application.
  </>
);

function getRequirementsItems(
  moveInDate?: string,
  currentPayoutPlanType?: 'Variable' | 'Fixed' | 'VariableNoMinimum',
): Item[] {
  const usesFixedQualificationRules =
    currentPayoutPlanType === 'Fixed' ||
    currentPayoutPlanType === 'VariableNoMinimum';

  const incomeDescription = usesFixedQualificationRules
    ? INCOME_REQUIREMENT_FIXED
    : INCOME_REQUIREMENT_VARIABLE;

  return [
    {
      id: 'income',
      description: incomeDescription,
      icon: usesFixedQualificationRules
        ? '/residents/bill-2.5x.svg'
        : '/residents/bill-3x.svg',
      iconClassName: 'md:w-[80px] md:h-[80px]',
    },
    {
      id: 'credit',
      description: (
        <>
          We usually require a{' '}
          <span className="font-semibold">700+ credit score</span>, but lower
          scores may qualify with a higher deposit or with a guarantor. We only
          consider the credit scores of the main income&nbsp;earner(s)*.
        </>
      ),
      icon: '/residents/application-key-factor-credit.svg',
    },
    {
      id: 'records',
      description: (
        <>
          We check public records to confirm a history free of evictions,
          bankruptcies, and criminal records,&nbsp;
          <span className="font-semibold">
            subject to state and city&nbsp;regulations
          </span>
          .
        </>
      ),
      icon: '/residents/application-key-factor-background-checks.svg',
    },
    {
      id: 'landlord',
      description: (
        <>
          A positive{' '}
          <span className="font-semibold">
            reference from your last landlord
          </span>{' '}
          is&nbsp;required. If you don’t have one, we’ll review your
          case&nbsp;individually.
        </>
      ),
      icon: '/residents/application-key-factor-references.svg',
    },
    {
      id: 'move-in-date',
      description: (
        <>
          {moveInDate && (
            <>
              Ready to move in{' '}
              <span className="font-semibold">
                {getMoveInTimingLabel(moveInDate)}
              </span>
            </>
          )}{' '}
          Later dates may be considered on a case-by-case&nbsp;basis.
        </>
      ),
      icon: '/residents/calendar-icon.svg',
      iconClassName: 'md:w-[80px] md:h-[80px]',
    },
  ];
}
const RequirementItem: React.FC<{
  description: React.ReactNode;
  icon: string;
  iconClassName?: string;
}> = ({ description, icon, iconClassName }) => (
  <div className="gap-md flex items-center">
    <div className="h-2xl w-2xl md:h-3xl md:w-3xl flex items-center justify-center">
      <Image
        src={icon}
        alt=""
        className={clsx('h-2xl w-2xl md:h-3xl md:w-3xl', iconClassName)}
      />
    </div>
    <div className="flex-1">
      <p className="mt-xs text-p1 leading-p1 text-navy md:text-body md:leading-body">
        {description}
      </p>
    </div>
  </div>
);

const BelongRequirements: React.FC<{
  moveInDate?: string;
  currentPayoutPlanType?: 'Variable' | 'Fixed' | 'VariableNoMinimum';
}> = ({ moveInDate, currentPayoutPlanType }) => {
  const usesFixedQualificationRules =
    currentPayoutPlanType === 'Fixed' ||
    currentPayoutPlanType === 'VariableNoMinimum';
  const items = getRequirementsItems(moveInDate, currentPayoutPlanType);
  return (
    <div className="gap-sm z-0 flex w-full flex-col items-center justify-center">
      <div className="flex w-full justify-center">
        <div className="p-sm md:p-xl max-w-[730px] rounded  bg-white shadow">
          {/* Header */}
          <header className="mb-sm md:mb-xl text-center">
            <h2 className="text-h3-xl text-navy font-semibold">Requirements</h2>
            <p className="mt-2xs text-p1 text-dark-gray">
              For Instant Qualification
            </p>
          </header>

          {/* Card */}
          <div>
            <div className="gap-xs grid grid-cols-1">
              {items.map((it) => (
                <RequirementItem
                  key={it.id}
                  description={it.description}
                  icon={it.icon}
                  iconClassName={it.iconClassName}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <Text
        variant="p2"
        className="leading-p2 text-dark-gray md:text-p1 w-[730px] max-w-full text-left"
      >
        * The person or group whose income is at least{' '}
        {usesFixedQualificationRules ? '2.5x' : '3x'} the monthly rent.
      </Text>
    </div>
  );
};

export default BelongRequirements;
